import { eventsFlux, EventTypes, NodeProvider, nodesFlux, useEditorSelector } from '@candulabs/craft';
import isEmpty from 'lodash/isEmpty';
import React, { ReactNode, useContext, useLayoutEffect, useMemo, useRef } from 'react';
import { createStructuredSelector } from 'reselect';

import { getNodeId } from '@candu-ai/styleguide/selectors';
import { BuiltInComponents, isRootNode } from 'craft/constants';
import { CollectionContext } from 'craft/contexts';
import {
  useMediaBreakpointClassNames,
  useNodeContext,
  usePropsWithCollection,
  useResponsiveCss,
  useSelectionClassName,
} from 'craft/hooks';
import { Text } from 'craft/resolvers/Text';

import { StyledNode } from './StyledNode';

type Props<T extends React.ElementType> = {
  children?: ReactNode;
  innerRef?: React.Ref<any>;
  is?: T;
  css?: any;
  // TODO this is probably no the best way around this ?
  [key: string]: any;
} & React.ComponentProps<T>;

/**
 * A node component wraps `block` to handle their drag and drop functions.
 * It also provides some normalization of the craft state and tree.
 *
 * @param cssProp
 * @param children
 * @constructor
 */
function Node<T extends React.ElementType>({
  children,
  is = 'div',
  innerRef,
  applyNodeProps = true,
  ...componentProps
}: Props<T>) {
  const {
    id,
    connectors: { connect, drag },
  } = useNodeContext();
  const { id: collectionItemId } = useContext(CollectionContext);

  const structuredSelector = useMemo(
    () =>
      createStructuredSelector({
        childrenNodes: nodesFlux.selectors.getNodeChildren(id),
        exists: nodesFlux.selectors.doesNodeExist(id),
        props: nodesFlux.selectors.getNodeProps(id),
        name: nodesFlux.selectors.getNodeName(id),
        isCanvas: nodesFlux.selectors.getIsNodeCanvas(id),
        isElementDraggedOver: eventsFlux.selectors.doesNodeHaveEvent(id, EventTypes.draggedOver),
        isElementHovered: eventsFlux.selectors.doesNodeHaveEvent(id, EventTypes.hovered),
      }),
    [id],
  );
  const {
    exists,
    props,
    name,
    isCanvas,
    childrenNodes,
    isElementDraggedOver,
    isElementHovered,
  } = useEditorSelector(structuredSelector);

  const domRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const { current: dom } = domRef;

    if (!dom || !exists) {
      return;
    }

    if (innerRef) {
      innerRef(dom);
    }
    connect(dom);

    // Dont add drag connector if it's the Root node
    // TODO(prev): this should be handled in Craft directly
    if (isRootNode(id)) {
      return;
    }

    drag(dom);
  });

  const breakpointClassNames = useMediaBreakpointClassNames(id);
  const { responsiveCss: css } = useResponsiveCss([id]);

  const selectedClassNames = useSelectionClassName(
    id,
    collectionItemId,
    name === BuiltInComponents.Collection,
  );
  const propsWithSource = usePropsWithCollection(id);
  const isMissingContentCollectionItem = Object.values(propsWithSource).some(
    (entry) => entry === null,
  );

  // Hack. If the  component defines additional props, then we pass those in
  // instead of passing the editor props. We need this when the component
  // changes the rendered props to have custom logic, like in `Badge` or `Button`.

  const elementProps = isEmpty(componentProps) ? props : componentProps;
  const className = `${elementProps?.className || ''} ${breakpointClassNames} ${
    isMissingContentCollectionItem ? 'collection-item--hidden' : ''
  }
  ${isElementDraggedOver ? 'draggedOver' : ''}
  ${isElementHovered ? 'hovered' : ''}
  ${selectedClassNames}
  `.trim();

  /*
    If we use a collection's value instead of hardcoded text here (means if propsWithSource.Text is valid), we want to retrieve
    only the first child node of the current Node, and replace the childrenString with the value from collection.
    A not so hacky way of doing this is by injecting a Node, wrapped by the craft's NodeProvider, id being the actual first children of the current Node
  */

  let sanitizedChildren = children;
  if (propsWithSource.Text && childrenNodes?.[0]) {
    sanitizedChildren = (
      <NodeProvider id={childrenNodes[0]}>
        <Text childrenString={propsWithSource.Text} />
      </NodeProvider>
    );
  } else if (propsWithSource.Text) {
    sanitizedChildren = <Text childrenString={propsWithSource.Text} />;
  }

  return React.createElement(
    is,
    {
      id: getNodeId(id),
      ...(applyNodeProps ? elementProps : {}),
      ...propsWithSource,
      className: `${className} editor-node`,
      style: applyNodeProps
        ? {
            ...(css ?? {}),
            ...(componentProps.style ?? {}),
          }
        : // used by flowsteps to fully override and customize
          // css rendering for a given node
          componentProps.overrideCss ?? {},
      tabIndex: 0,
      ref: domRef,
      'data-cy': `node-${name?.toLowerCase()}`,
      'data-nid': collectionItemId ? `${id}-${collectionItemId}` : id,
      'data-canvas': isCanvas,
      'data-collection-item-id': collectionItemId || undefined,
    },
    sanitizedChildren,
  );
}

export default StyledNode(Node);
